import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  FormControlLabel,
  RadioGroup,
  Radio,
  ListSubheader,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { OrganizationType } from '../../models/Organization';
import {
  PassOfferStatus,
  PassOfferSummary,
  PassOfferUpsertRequest,
  PassTargetType,
  PassUnitType,
  PassValidityStartPolicy,
} from '../../models/Pass';
import { LessonOptionSummaryResponse } from '../../models/LessonOption';
import { Room } from '../../models/Room';
import { getTutorSessionTypeLabel } from '../../utils/tutorSessionType';
import type { PassFormLocationState } from '../../utils/passDuplicate';
import { defaultHostPassTargetType, hostCanCreateRehearsalPasses } from '../../utils/passHostAccess';
import PassOfferStatusChip from '../../components/passes/PassOfferStatusChip';

const DEFAULT_VALIDITY_DAYS = 30;
const DEFAULT_ROOM_QUANTITY_MINUTES = 240; // 4 hours
const DEFAULT_LESSON_QUANTITY = 4;
const FALLBACK_CURRENCY = 'PLN';

const unitTypeForTarget = (targetType: PassTargetType): PassUnitType =>
  targetType === 'ROOM' ? 'MINUTE' : 'COUNT';

const defaultQuantityForTarget = (targetType: PassTargetType): number =>
  targetType === 'ROOM' ? DEFAULT_ROOM_QUANTITY_MINUTES : DEFAULT_LESSON_QUANTITY;

/** API stores ROOM entitlement as minutes; host UI edits hours. */
const minutesToHours = (minutes: number): number => {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? hours : Math.round(hours * 100) / 100;
};

const hoursToMinutes = (hours: number): number => Math.round(hours * 60);

const quantityToInputText = (quantity: number, unitType: PassUnitType): string =>
  String(unitType === 'MINUTE' ? minutesToHours(quantity) : quantity);

const parseOptionalNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
};

type PassFormField = 'title' | 'targetIds' | 'quantity' | 'price' | 'validity';

const emptyForm: PassOfferUpsertRequest = {
  title: '',
  targetType: 'ROOM',
  targetIds: [],
  quantity: DEFAULT_ROOM_QUANTITY_MINUTES,
  unitType: 'MINUTE',
  fixedPrice: 0,
  currencyCode: FALLBACK_CURRENCY,
  validityStartPolicy: 'FROM_PURCHASE',
  validityDurationDays: DEFAULT_VALIDITY_DAYS,
};

const HostPassFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<PassOfferUpsertRequest>(emptyForm);
  const [quantityInput, setQuantityInput] = useState(
    quantityToInputText(emptyForm.quantity, emptyForm.unitType)
  );
  const [priceInput, setPriceInput] = useState('');
  const [validityInput, setValidityInput] = useState(String(DEFAULT_VALIDITY_DAYS));
  const [touched, setTouched] = useState<Partial<Record<PassFormField, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [status, setStatus] = useState<PassOfferStatus | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [spaceNameById, setSpaceNameById] = useState<Map<string, string>>(new Map());
  const [spaceCurrencyById, setSpaceCurrencyById] = useState<Map<string, string>>(new Map());
  const [lessonOptions, setLessonOptions] = useState<LessonOptionSummaryResponse[]>([]);
  const [existingPassOffers, setExistingPassOffers] = useState<PassOfferSummary[]>([]);
  const [loading, setLoading] = useState(isEditMode);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [lessonOptionsLoading, setLessonOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState<'draft' | 'activate' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | undefined>();
  const [returnToLessonOptionId] = useState<string | null>(
    () => (location.state as PassFormLocationState | null)?.returnToLessonOptionId ?? null
  );
  const [returnToSpaceId] = useState<string | null>(
    () => (location.state as PassFormLocationState | null)?.returnToSpaceId ?? null
  );
  const createDefaultsLockedRef = useRef(isEditMode);

  useEffect(() => {
    let cancelled = false;
    void hostApiClient
      .getOrganization()
      .then((organization) => {
        if (!cancelled) {
          setOrganizationType(organization.organizationType);
        }
      })
      .catch((err) => {
        console.error('Error fetching organization type for pass form:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (organizationType === OrganizationType.INDIVIDUAL_TUTOR) {
      return;
    }

    const fetchRooms = async () => {
      try {
        setRoomsLoading(true);
        const [roomList, spaceList] = await Promise.all([
          hostApiClient.getRooms(),
          hostApiClient.getSpaces(),
        ]);
        const spaceNameById = new Map(spaceList.map((space) => [space.id, space.name]));
        const spaceCurrencyById = new Map(
          spaceList.map((space) => [space.id, space.currencyCode?.toString() || FALLBACK_CURRENCY])
        );
        setRooms(
          [...roomList].sort((a, b) => {
            const spaceA = spaceNameById.get(a.spaceId) || '';
            const spaceB = spaceNameById.get(b.spaceId) || '';
            const spaceCmp = spaceA.localeCompare(spaceB);
            if (spaceCmp !== 0) return spaceCmp;
            return a.name.localeCompare(b.name);
          })
        );
        setSpaceNameById(spaceNameById);
        setSpaceCurrencyById(spaceCurrencyById);
      } catch (err) {
        console.error('Error fetching rooms for pass target picker:', err);
      } finally {
        setRoomsLoading(false);
      }
    };

    void fetchRooms();
  }, [organizationType]);

  useEffect(() => {
    const fetchLessonOptions = async () => {
      try {
        setLessonOptionsLoading(true);
        const data = await hostApiClient.listLessonOptions();
        setLessonOptions(
          [...data].sort((a, b) => {
            if (a.status !== b.status) {
              return a.status === 'ACTIVE' ? -1 : 1;
            }
            return a.name.localeCompare(b.name);
          })
        );
      } catch (err) {
        console.error('Error fetching lesson options for pass target picker:', err);
      } finally {
        setLessonOptionsLoading(false);
      }
    };

    fetchLessonOptions();
  }, []);

  useEffect(() => {
    const fetchExistingPassOffers = async () => {
      try {
        const [offers, archived] = await Promise.all([
          hostApiClient.listPassOffers(),
          hostApiClient.listPassOffers(['ARCHIVED']),
        ]);
        setExistingPassOffers([...offers, ...archived]);
      } catch (err) {
        console.error('Error fetching pass offers for duplicate-name check:', err);
      }
    };

    fetchExistingPassOffers();
  }, []);

  useEffect(() => {
    if (id) {
      return;
    }

    const state = location.state as PassFormLocationState | null;
    const duplicateFrom = state?.duplicateFrom;
    const prefillTarget = state?.prefillTarget;
    if (!duplicateFrom && !prefillTarget) {
      return;
    }

    if (duplicateFrom) {
      createDefaultsLockedRef.current = true;
      setForm({
        title: duplicateFrom.title,
        targetType: duplicateFrom.targetType,
        targetIds: [...(duplicateFrom.targetIds ?? [])],
        quantity: duplicateFrom.quantity,
        unitType: duplicateFrom.unitType,
        fixedPrice: duplicateFrom.fixedPrice,
        currencyCode: duplicateFrom.currencyCode,
        validityStartPolicy: duplicateFrom.validityStartPolicy,
        validityDurationDays: duplicateFrom.validityDurationDays,
      });
      setQuantityInput(quantityToInputText(duplicateFrom.quantity, duplicateFrom.unitType));
      setPriceInput(String(duplicateFrom.fixedPrice));
      setValidityInput(String(duplicateFrom.validityDurationDays));
      setStatus(null);
    } else if (
      prefillTarget &&
      (prefillTarget.targetType === 'LESSON_OPTION' || prefillTarget.targetType === 'ROOM') &&
      (prefillTarget.targetIds?.length ?? 0) > 0
    ) {
      createDefaultsLockedRef.current = true;
      const unitType = unitTypeForTarget(prefillTarget.targetType);
      const quantity = defaultQuantityForTarget(prefillTarget.targetType);
      setForm((prev) => ({
        ...prev,
        targetType: prefillTarget.targetType,
        targetIds: [...prefillTarget.targetIds],
        unitType,
        quantity,
        currencyCode: prefillTarget.currencyCode?.trim() || prev.currencyCode,
      }));
      setQuantityInput(quantityToInputText(quantity, unitType));
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [id, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (isEditMode || createDefaultsLockedRef.current) {
      return;
    }
    if (organizationType !== OrganizationType.INDIVIDUAL_TUTOR) {
      return;
    }

    createDefaultsLockedRef.current = true;
    const targetType = defaultHostPassTargetType(organizationType);
    const unitType = unitTypeForTarget(targetType);
    const quantity = defaultQuantityForTarget(targetType);
    setForm((prev) => ({
      ...prev,
      targetType,
      targetIds: [],
      unitType,
      quantity,
    }));
    setQuantityInput(quantityToInputText(quantity, unitType));
  }, [isEditMode, organizationType]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchPass = async () => {
      try {
        setLoading(true);
        const data = await hostApiClient.getPassOffer(id);
        setForm({
          title: data.title,
          targetType: data.targetType,
          targetIds: [...(data.targetIds ?? [])],
          quantity: data.quantity,
          unitType: data.unitType,
          fixedPrice: data.fixedPrice,
          currencyCode: data.currencyCode,
          validityStartPolicy: data.validityStartPolicy,
          validityDurationDays: data.validityDurationDays,
        });
        setQuantityInput(quantityToInputText(data.quantity, data.unitType));
        setPriceInput(String(data.fixedPrice));
        setValidityInput(String(data.validityDurationDays));
        setStatus(data.status);
      } catch (err) {
        setError(t('hostPasses.fetchError', 'Failed to fetch pass offer'));
        console.error(`Error fetching host pass offer ${id}:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchPass();
  }, [id, t]);

  const knownLessonOptionIds = useMemo(
    () => new Set(lessonOptions.map((option) => option.id)),
    [lessonOptions]
  );

  const unknownLessonOptionIds = useMemo(
    () =>
      form.targetType === 'LESSON_OPTION'
        ? (form.targetIds ?? []).filter((id) => !knownLessonOptionIds.has(id))
        : [],
    [form.targetType, form.targetIds, knownLessonOptionIds]
  );

  const hasUnknownLessonOptionTarget =
    unknownLessonOptionIds.length > 0 && !lessonOptionsLoading;

  const knownRoomIds = useMemo(() => new Set(rooms.map((room) => room.id)), [rooms]);

  const unknownRoomIds = useMemo(
    () =>
      form.targetType === 'ROOM'
        ? (form.targetIds ?? []).filter((id) => !knownRoomIds.has(id))
        : [],
    [form.targetType, form.targetIds, knownRoomIds]
  );

  const hasUnknownRoomTarget = unknownRoomIds.length > 0 && !roomsLoading;

  const selectedRooms = useMemo(
    () => rooms.filter((room) => (form.targetIds ?? []).includes(room.id)),
    [rooms, form.targetIds]
  );

  const selectedLessonOptions = useMemo(
    () => lessonOptions.filter((option) => (form.targetIds ?? []).includes(option.id)),
    [lessonOptions, form.targetIds]
  );

  const selectedRoom = selectedRooms[0];
  const selectedLessonOption = selectedLessonOptions[0];

  const displayCurrency = useMemo(() => {
    if (form.targetType === 'ROOM') {
      const fromSpace = selectedRoom
        ? spaceCurrencyById.get(selectedRoom.spaceId)
        : undefined;
      if (fromSpace) return fromSpace;
    } else if (selectedLessonOption?.currencyCode) {
      return selectedLessonOption.currencyCode;
    }
    return form.currencyCode || FALLBACK_CURRENCY;
  }, [form.targetType, form.currencyCode, selectedRoom, spaceCurrencyById, selectedLessonOption]);

  const roomSelectGroups = useMemo(() => {
    const groups: { spaceId: string; spaceName: string; rooms: Room[] }[] = [];
    for (const room of rooms) {
      const last = groups[groups.length - 1];
      if (last && last.spaceId === room.spaceId) {
        last.rooms.push(room);
        continue;
      }
      groups.push({
        spaceId: room.spaceId,
        spaceName: spaceNameById.get(room.spaceId) || t('hostPasses.unknownSpace', 'Space'),
        rooms: [room],
      });
    }
    return groups;
  }, [rooms, spaceNameById, t]);

  useEffect(() => {
    if (form.currencyCode === displayCurrency) return;
    setForm((prev) => ({ ...prev, currencyCode: displayCurrency }));
  }, [displayCurrency, form.currencyCode]);

  const handleTargetTypeChange = (_event: React.MouseEvent<HTMLElement>, targetType: PassTargetType | null) => {
    if (!targetType) return;
    const unitType = unitTypeForTarget(targetType);
    const quantity = defaultQuantityForTarget(targetType);
    setForm((prev) => ({
      ...prev,
      targetType,
      targetIds: [],
      unitType,
      quantity,
      currencyCode: FALLBACK_CURRENCY,
    }));
    setQuantityInput(quantityToInputText(quantity, unitType));
    setTouched({});
    setSubmitAttempted(false);
    setError(null);
  };

  const markTouched = (field: PassFormField) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleTargetRoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value as unknown;
    const targetIds = Array.isArray(raw)
      ? (raw as string[])
      : typeof raw === 'string' && raw.length > 0
        ? raw.split(',')
        : [];
    const firstRoom = rooms.find((item) => item.id === targetIds[0]);
    setForm((prev) => ({
      ...prev,
      targetIds,
      currencyCode:
        (firstRoom ? spaceCurrencyById.get(firstRoom.spaceId) : undefined) || FALLBACK_CURRENCY,
    }));
  };

  const handleTargetLessonOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value as unknown;
    const targetIds = Array.isArray(raw)
      ? (raw as string[])
      : typeof raw === 'string' && raw.length > 0
        ? raw.split(',')
        : [];
    const firstOption = lessonOptions.find((item) => item.id === targetIds[0]);
    setForm((prev) => ({
      ...prev,
      targetIds,
      currencyCode: firstOption?.currencyCode || FALLBACK_CURRENCY,
    }));
  };

  const handleTextFieldChange = (field: keyof PassOfferUpsertRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleQuantityInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityInput(event.target.value);
  };

  const handlePriceInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    // Whole currency units only for now (no fractional amounts).
    if (raw === '' || /^\d+$/.test(raw)) {
      setPriceInput(raw);
    }
  };

  const handleValidityInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidityInput(event.target.value);
  };

  const parsedQuantityDisplay = parseOptionalNumber(quantityInput);
  const parsedPrice = parseOptionalNumber(priceInput);
  const parsedValidityDays = parseOptionalNumber(validityInput);

  const parsedQuantityApi =
    parsedQuantityDisplay === null
      ? null
      : form.unitType === 'MINUTE'
        ? hoursToMinutes(parsedQuantityDisplay)
        : parsedQuantityDisplay;

  const pricePerLesson =
    form.targetType === 'LESSON_OPTION' &&
    parsedQuantityApi !== null &&
    parsedQuantityApi > 0 &&
    parsedPrice !== null
      ? Math.round((parsedPrice / parsedQuantityApi) * 100) / 100
      : null;

  const pricePerHour =
    form.targetType === 'ROOM' &&
    parsedQuantityDisplay !== null &&
    parsedQuantityDisplay > 0 &&
    parsedPrice !== null
      ? Math.round((parsedPrice / parsedQuantityDisplay) * 100) / 100
      : null;

  const formatMoneyAmount = (amount: number): string =>
    Number.isInteger(amount) ? String(amount) : amount.toFixed(2);

  const fieldErrors: Record<PassFormField, string | null> = {
    title: form.title.trim()
      ? null
      : t('hostPasses.fieldRequiredPassName', 'Enter a pass name'),
    targetIds:
      (form.targetIds ?? []).length > 0
        ? null
        : form.targetType === 'ROOM'
          ? t('hostPasses.fieldRequiredRooms', 'Choose at least one room')
          : t('hostPasses.fieldRequiredLessonOptions', 'Choose at least one lesson option'),
    quantity:
      parsedQuantityApi !== null && parsedQuantityApi > 0
        ? null
        : form.targetType === 'ROOM'
          ? t('hostPasses.fieldRequiredHours', 'Enter rehearsal hours')
          : t('hostPasses.fieldRequiredLessons', 'Enter number of lessons'),
    price:
      parsedPrice !== null && parsedPrice >= 0 && Number.isInteger(parsedPrice)
        ? null
        : t('hostPasses.fieldRequiredPrice', 'Enter a whole-number price'),
    validity:
      parsedValidityDays !== null && parsedValidityDays > 0
        ? null
        : t('hostPasses.fieldRequiredValidity', 'Enter how many days the pass is valid'),
  };

  const duplicateWarning = useMemo(() => {
    const trimmedTitle = form.title.trim();
    if (!trimmedTitle) return null;
    const isDuplicate = existingPassOffers.some(
      (offer) =>
        offer.id !== id &&
        offer.title.trim().toLocaleLowerCase() === trimmedTitle.toLocaleLowerCase()
    );
    return isDuplicate
      ? t('hostPasses.duplicateTitle', 'A pass offer with this name already exists')
      : null;
  }, [existingPassOffers, form.title, id, t]);

  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  useEffect(() => {
    setShowDuplicateWarning(false);
  }, [form.title]);

  const shouldShowFieldError = (field: PassFormField): boolean =>
    Boolean(fieldErrors[field]) && (submitAttempted || Boolean(touched[field]));

  const isFormValid = Object.values(fieldErrors).every((message) => message === null);

  const handleSubmit = async (mode: 'draft' | 'activate') => {
    setSubmitAttempted(true);

    if (status === 'ARCHIVED') {
      setShowDuplicateWarning(false);
      setError(
        t(
          'hostPasses.archivedReadOnlyHint',
          'This offer is archived and can’t be edited. Manage it from the Archive tab.'
        )
      );
      return;
    }

    if (
      !isFormValid ||
      parsedQuantityApi === null ||
      parsedPrice === null ||
      parsedValidityDays === null
    ) {
      setShowDuplicateWarning(false);
      setError(t('hostPasses.validationError', 'Please fill in all required fields'));
      return;
    }

    if (duplicateWarning) {
      setError(null);
      setShowDuplicateWarning(true);
      return;
    }
    setShowDuplicateWarning(false);

    const payload: PassOfferUpsertRequest = {
      ...form,
      targetIds: Array.from(new Set((form.targetIds ?? []).filter(Boolean))),
      quantity: parsedQuantityApi,
      fixedPrice: parsedPrice,
      validityDurationDays: parsedValidityDays,
      currencyCode: displayCurrency,
      validityStartPolicy:
        form.validityStartPolicy === 'FROM_FIRST_USE' ? 'FROM_FIRST_USE' : 'FROM_PURCHASE',
      unitType: unitTypeForTarget(form.targetType),
    };

    try {
      setSaving(true);
      setSavingMode(mode);
      setError(null);

      if (isEditMode && id) {
        const previousStatus = status;
        await hostApiClient.updatePassOffer(id, payload);

        if (mode === 'activate') {
          if (previousStatus !== 'ACTIVE') {
            await hostApiClient.activatePassOffer(id);
          }
          navigate(`/host/passes/${id}`, {
            replace: true,
            state: {
              flashMessage:
                previousStatus !== 'ACTIVE'
                  ? t('hostPasses.activatedSuccess', 'Pass offer activated')
                  : t('hostPasses.updatedSuccess', 'Pass offer saved'),
            },
          });
          return;
        }

        if (previousStatus === 'ACTIVE') {
          await hostApiClient.deactivatePassOffer(id);
        }
        navigate(`/host/passes/${id}`, {
          replace: true,
          state: {
            flashMessage:
              previousStatus === 'ACTIVE'
                ? t('hostPasses.deactivatedSuccess', 'Pass offer moved to draft')
                : t('hostPasses.savedAsDraft', 'Pass offer saved as draft'),
          },
        });
        return;
      }

      const created = await hostApiClient.createPassOffer(payload);
      if (mode === 'activate') {
        await hostApiClient.activatePassOffer(created.id);
        navigate(`/host/passes/${created.id}`, {
          replace: true,
          state: {
            flashMessage: t(
              'hostPasses.createdAndActivated',
              'Pass offer created and activated'
            ),
          },
        });
        return;
      }

      navigate(`/host/passes/${created.id}`, {
        replace: true,
        state: {
          flashMessage: t(
            'hostPasses.createdNotOnSale',
            'Pass offer created as a draft'
          ),
        },
      });
    } catch (err) {
      if (mode === 'activate') {
        setError(t('hostPasses.activateError', 'Failed to activate pass offer'));
      } else if (mode === 'draft' && status === 'ACTIVE') {
        setError(t('hostPasses.deactivateError', 'Failed to deactivate pass offer'));
      } else {
        setError(
          isEditMode
            ? t('hostPasses.updateError', 'Failed to update pass offer')
            : t('hostPasses.createError', 'Failed to create pass offer')
        );
      }
      console.error('Error saving host pass offer:', err);
    } finally {
      setSaving(false);
      setSavingMode(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const quantityLabel =
    form.targetType === 'ROOM'
      ? t('hostPasses.hoursIncluded', 'Rehearsal hours')
      : t('hostPasses.numberOfLessons', 'Number of lessons');

  const quantityHelper =
    form.targetType === 'ROOM'
      ? t('hostPasses.quantityHelperHours', 'Hours are taken from the pack based on booking length.')
      : t(
          'hostPasses.quantityHelperLessons',
          'One lesson is taken from the pack for each booking.'
        );

  const passFormExitPath =
    isEditMode && id
      ? `/host/passes/${id}`
      : returnToLessonOptionId
        ? `/host/lesson-options/${returnToLessonOptionId}`
        : returnToSpaceId
          ? `/host/spaces/${returnToSpaceId}`
          : '/host/passes';
  const passFormBackLabel = isEditMode
    ? t('hostPasses.details.backToOffer', 'Back to pass')
    : returnToLessonOptionId
      ? t('hostLessonOptions.details.backToLesson', 'Back to lesson')
      : returnToSpaceId
        ? t('spaceDetails.backToSpace', 'Back to space')
        : t('hostPasses.backToPasses', 'Back to passes');
  const showRehearsalToggle = hostCanCreateRehearsalPasses(organizationType);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 800, width: '100%', mx: 'auto' }}>
      <Chip
        label={`← ${passFormBackLabel}`}
        onClick={() => navigate(passFormExitPath)}
        sx={{
          mb: { xs: 1, sm: 2 },
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          bgcolor: 'transparent',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'transparent' },
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {showDuplicateWarning && duplicateWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setShowDuplicateWarning(false)}>
          {duplicateWarning}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            {isEditMode
              ? t('hostPasses.editTitle', 'Edit pass')
              : t('hostPasses.createTitle', 'Create pass')}
          </Typography>
          {isEditMode && status && <PassOfferStatusChip status={status} />}
        </Box>
        {isEditMode && status === 'ARCHIVED' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              'hostPasses.archivedReadOnlyHint',
              'This offer is archived and can’t be edited. Manage it from the Archive tab.'
            )}
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'hostPasses.visibilityHint',
              'Students can buy this pass only while it is active. It appears on the room or lesson you attach it to.'
            )}
          </Typography>
        )}
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <TextField
            fullWidth
            label={t('hostPasses.passName', 'Pass name')}
            value={form.title}
            onChange={handleTextFieldChange('title')}
            onBlur={markTouched('title')}
            placeholder={
              form.targetType === 'ROOM'
                ? t('hostPasses.passNamePlaceholderSpace', 'e.g. 10 rehearsal hours')
                : t('hostPasses.passNamePlaceholderLessons', 'e.g. 4 guitar lessons')
            }
            InputLabelProps={{ shrink: true }}
            error={shouldShowFieldError('title')}
            helperText={shouldShowFieldError('title') ? fieldErrors.title : undefined}
            required
          />

          {showRehearsalToggle ? (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('hostPasses.passType', 'Pass type')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={form.targetType}
              onChange={handleTargetTypeChange}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                },
              }}
            >
              <ToggleButton value="ROOM">
                {t('hostPasses.targetTypeRehearsalPack', 'Rehearsal')}
              </ToggleButton>
              <ToggleButton value="LESSON_OPTION">
                {t('hostPasses.targetTypeLessonPack', 'Lesson')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          ) : null}

          {form.targetType === 'ROOM' ? (
            rooms.length === 0 && !roomsLoading && !hasUnknownRoomTarget ? (
            <Alert severity="warning">
              {t(
                'hostPasses.targetRoomEmpty',
                'No rooms yet. Add them under Spaces first.'
              )}
            </Alert>
            ) : (
            <TextField
              select
              fullWidth
              required
              label={t('hostPasses.targetRooms', 'Rooms')}
              value={form.targetIds ?? []}
              onChange={handleTargetRoomChange}
              onBlur={markTouched('targetIds')}
              disabled={roomsLoading}
              InputLabelProps={{ shrink: true }}
              error={shouldShowFieldError('targetIds')}
              helperText={
                shouldShowFieldError('targetIds')
                  ? fieldErrors.targetIds
                  : hasUnknownRoomTarget
                    ? t(
                        'hostPasses.targetRoomsUnknownHelper',
                        'Some saved rooms are no longer in your catalog; keep or replace them.'
                      )
                    : t(
                        'hostPasses.targetRoomsHelper',
                        'Select one or more rooms this pass can be used for.'
                      )
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((id) => {
                      const room = rooms.find((item) => item.id === id);
                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={
                            room?.name ||
                            t('hostPasses.targetRoomUnknown', 'Saved room ({{id}})', { id })
                          }
                        />
                      );
                    })}
                  </Box>
                ),
              }}
            >
              {unknownRoomIds.map((id) => (
                <MenuItem key={`unknown-room-${id}`} value={id}>
                  <Checkbox checked={(form.targetIds ?? []).includes(id)} tabIndex={-1} disableRipple />
                  <ListItemText
                    primary={t('hostPasses.targetRoomUnknown', 'Saved room ({{id}})', { id })}
                  />
                </MenuItem>
              ))}
              {roomSelectGroups.flatMap((group) => [
                <ListSubheader
                  key={`space-${group.spaceId}`}
                  disableSticky
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {group.spaceName}
                </ListSubheader>,
                ...group.rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    <Checkbox
                      checked={(form.targetIds ?? []).includes(room.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={room.name} />
                  </MenuItem>
                )),
              ])}
            </TextField>
            )
          ) : lessonOptions.length === 0 && !lessonOptionsLoading && !hasUnknownLessonOptionTarget ? (
            <Alert severity="warning">
              {t(
                'hostPasses.targetLessonOptionEmpty',
                'No lessons in your catalog yet. Add them under Lessons first.'
              )}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              required
              label={t('hostPasses.targetLessonOptions', 'Lesson options')}
              value={form.targetIds ?? []}
              onChange={handleTargetLessonOptionChange}
              onBlur={markTouched('targetIds')}
              disabled={lessonOptionsLoading}
              InputLabelProps={{ shrink: true }}
              error={shouldShowFieldError('targetIds')}
              helperText={
                shouldShowFieldError('targetIds')
                  ? fieldErrors.targetIds
                  : hasUnknownLessonOptionTarget
                    ? t(
                        'hostPasses.targetLessonOptionsUnknownHelper',
                        'Some saved options are no longer in the catalog; keep or replace them.'
                      )
                    : t(
                        'hostPasses.targetLessonOptionsHelper',
                        'Select one or more lesson options this pass can be used for.'
                      )
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((id) => {
                      const option = lessonOptions.find((item) => item.id === id);
                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={
                            option?.name ||
                            t('hostPasses.targetLessonOptionUnknown', 'Saved lesson option ({{id}})', {
                              id,
                            })
                          }
                        />
                      );
                    })}
                  </Box>
                ),
              }}
            >
              {unknownLessonOptionIds.map((id) => (
                <MenuItem key={`unknown-lesson-${id}`} value={id}>
                  <Checkbox checked={(form.targetIds ?? []).includes(id)} tabIndex={-1} disableRipple />
                  <ListItemText
                    primary={t('hostPasses.targetLessonOptionUnknown', 'Saved lesson option ({{id}})', {
                      id,
                    })}
                  />
                </MenuItem>
              ))}
              {lessonOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox
                    checked={(form.targetIds ?? []).includes(option.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={option.name}
                    secondary={[
                      getTutorSessionTypeLabel(option.sessionType, t),
                      `${option.durationMinutes} ${t('tutorCalendar.minutesShort', 'min')}`,
                      `${formatMoneyAmount(option.price)} ${option.currencyCode}`,
                      option.status !== 'ACTIVE'
                        ? t('hostPasses.statusNotOnSale', 'Draft')
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            type="number"
            label={quantityLabel}
            value={quantityInput}
            onChange={handleQuantityInputChange}
            onBlur={markTouched('quantity')}
            inputProps={form.unitType === 'MINUTE' ? { min: 0, step: 0.5 } : { min: 1, step: 1 }}
            InputLabelProps={{ shrink: true }}
            error={shouldShowFieldError('quantity')}
            helperText={shouldShowFieldError('quantity') ? fieldErrors.quantity : quantityHelper}
            required
          />

          <TextField
            fullWidth
            type="number"
            label={t('hostPasses.fixedPrice', 'Price')}
            value={priceInput}
            onChange={handlePriceInputChange}
            onBlur={markTouched('price')}
            inputProps={{ min: 0, step: 1 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{displayCurrency}</InputAdornment>,
            }}
            error={shouldShowFieldError('price')}
            helperText={
              shouldShowFieldError('price')
                ? fieldErrors.price
                : pricePerHour !== null
                  ? t(
                      'hostPasses.pricePerHourHelper',
                      '{{amount}} {{currency}} per hour',
                      {
                        amount: formatMoneyAmount(pricePerHour),
                        currency: displayCurrency,
                      }
                    )
                  : pricePerLesson !== null
                    ? t(
                        'hostPasses.pricePerLessonHelper',
                        '{{amount}} {{currency}} per lesson',
                        {
                          amount: formatMoneyAmount(pricePerLesson),
                          currency: displayCurrency,
                        }
                      )
                    : undefined
            }
            required
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              backgroundColor: 'background.default',
              borderRadius: 2,
              px: 3,
              py: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
              <Typography variant="body1" fontWeight="medium">
                {t('hostPasses.validityStartPolicy', 'Validity starts')}
              </Typography>
              <RadioGroup
                row
                value={
                  form.validityStartPolicy === 'FROM_FIRST_USE' ? 'FROM_FIRST_USE' : 'FROM_PURCHASE'
                }
                onChange={(event) => {
                  const policy = event.target.value as PassValidityStartPolicy;
                  setForm((prev) => ({
                    ...prev,
                    validityStartPolicy:
                      policy === 'FROM_FIRST_USE' ? 'FROM_FIRST_USE' : 'FROM_PURCHASE',
                  }));
                }}
                sx={{ mt: 0.5 }}
              >
                <FormControlLabel
                  value="FROM_PURCHASE"
                  control={<Radio size="small" />}
                  label={t('hostPasses.validityStartPolicyFromPurchase', 'From purchase')}
                />
                <FormControlLabel
                  value="FROM_FIRST_USE"
                  control={<Radio size="small" />}
                  label={t('hostPasses.validityStartPolicyFromFirstUse', 'From first use')}
                />
              </RadioGroup>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {form.validityStartPolicy === 'FROM_FIRST_USE'
                  ? t(
                      'hostPasses.validityStartsOnFirstUse',
                      'The validity period starts when the customer first uses the pass.'
                    )
                  : t(
                      'hostPasses.validityStartsOnPurchase',
                      'The pass activates when the customer buys it.'
                    )}
              </Typography>
            </Box>

            <TextField
              type="number"
              size="small"
              label={t('hostPasses.validFor', 'Valid for')}
              value={validityInput}
              onChange={handleValidityInputChange}
              onBlur={markTouched('validity')}
              inputProps={{ min: 1, step: 1 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {t('hostPasses.validForDaysUnit', 'days')}
                  </InputAdornment>
                ),
              }}
              error={shouldShowFieldError('validity')}
              helperText={shouldShowFieldError('validity') ? fieldErrors.validity : undefined}
              required
              sx={{ width: { xs: '100%', sm: 168 }, flexShrink: 0 }}
            />
          </Box>

          {parsedQuantityApi !== null &&
          parsedQuantityApi > 0 &&
          parsedValidityDays !== null &&
          parsedValidityDays > 0 &&
          parsedPrice !== null ? (
            <Typography variant="body2" color="text.secondary">
              {form.targetType === 'ROOM'
                ? form.validityStartPolicy === 'FROM_FIRST_USE'
                  ? t(
                      'hostPasses.summarySpaceFromFirstUse',
                      '{{hours}} rehearsal hours · {{pricePerHour}} {{currency}}/hour · {{days}} days from first use · {{price}} {{currency}}',
                      {
                        hours: parsedQuantityDisplay,
                        days: parsedValidityDays,
                        price: formatMoneyAmount(parsedPrice),
                        pricePerHour:
                          pricePerHour !== null ? formatMoneyAmount(pricePerHour) : '',
                        currency: displayCurrency,
                      }
                    )
                  : t(
                      'hostPasses.summarySpace',
                      '{{hours}} rehearsal hours · {{pricePerHour}} {{currency}}/hour · {{days}} days from purchase · {{price}} {{currency}}',
                      {
                        hours: parsedQuantityDisplay,
                        days: parsedValidityDays,
                        price: formatMoneyAmount(parsedPrice),
                        pricePerHour:
                          pricePerHour !== null ? formatMoneyAmount(pricePerHour) : '',
                        currency: displayCurrency,
                      }
                    )
                : form.validityStartPolicy === 'FROM_FIRST_USE'
                  ? t(
                      'hostPasses.summaryLessonsFromFirstUse',
                      '{{count}} lessons · {{pricePerLesson}} {{currency}}/lesson · {{days}} days from first use · {{price}} {{currency}}',
                      {
                        count: parsedQuantityApi,
                        days: parsedValidityDays,
                        price: formatMoneyAmount(parsedPrice),
                        pricePerLesson:
                          pricePerLesson !== null ? formatMoneyAmount(pricePerLesson) : '',
                        currency: displayCurrency,
                      }
                    )
                  : t(
                      'hostPasses.summaryLessons',
                      '{{count}} lessons · {{pricePerLesson}} {{currency}}/lesson · {{days}} days from purchase · {{price}} {{currency}}',
                      {
                        count: parsedQuantityApi,
                        days: parsedValidityDays,
                        price: formatMoneyAmount(parsedPrice),
                        pricePerLesson:
                          pricePerLesson !== null ? formatMoneyAmount(pricePerLesson) : '',
                        currency: displayCurrency,
                      }
                    )}
            </Typography>
          ) : null}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(passFormExitPath)}
              disabled={saving}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleSubmit('draft')}
                disabled={saving || status === 'ARCHIVED'}
              >
                {savingMode === 'draft' ? (
                  <CircularProgress size={20} />
                ) : (
                  t('hostPasses.saveAsDraft', 'Save as draft')
                )}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSubmit('activate')}
                disabled={saving || status === 'ARCHIVED'}
              >
                {savingMode === 'activate' ? (
                  <CircularProgress size={20} />
                ) : (
                  t('hostPasses.saveAndActivate', 'Save and activate')
                )}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
};

export default HostPassFormPage;
